"use client";

/**
 * LandingFoilMark — Foundry logo mark as a foil-holographic badge.
 *
 * This is NOT a grade badge. `FoilGradeBadge` is grade-semantic: it classifies
 * letters into tiers (S/A → emerald, B → amber, C → orange, everything else →
 * red) and shows numeric/letter grades. Scene 0 renders a brand mark, not a
 * grade — hardcoding "F" through FoilGradeBadge would misfire the tier logic
 * (F would classify as "low" = red). We replicate the same holographic visual
 * language (conic shimmer, radial highlight, noise ring, spring-bounce) but
 * pinned to the amber brand palette.
 *
 * Visual parity with FoilGradeBadge preserved:
 *   - Conic-gradient shimmer, 12s linear infinite (paused in reduced-motion)
 *   - Radial highlight top-left for glossy finish
 *   - Outer ring-1 white/10, inner rounded surface with subtle shadow
 *   - Spring-bounce on mount (spec §2.6: stiffness 200, damping 18, delay 100ms)
 *
 * Fallback: inline letter, no external asset. If `/public/brand/foundry-mark.svg`
 * is added later, swap the inner letter for an Image or inline SVG here.
 */

import { motion, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";

type LandingFoilMarkSize = "sm" | "md" | "lg";

interface LandingFoilMarkProps {
  /** 32 (navbar), 80 (mobile hero), or 120 (desktop hero). Maps via token, not raw pixels. */
  size?: LandingFoilMarkSize;
  /** Extra positioning classes. */
  className?: string;
  /** Spring delay (seconds). Spec §2.6 default = 0.1. */
  animationDelay?: number;
}

function dimensionPx(size: LandingFoilMarkSize): number {
  if (size === "lg") return 120;
  if (size === "sm") return 32;
  return 80;
}

function letterFontClass(size: LandingFoilMarkSize): string {
  if (size === "lg") return "text-5xl md:text-6xl";
  if (size === "sm") return "text-base";
  return "text-3xl md:text-4xl";
}

export function LandingFoilMark({
  size = "lg",
  className,
  animationDelay = 0.1,
}: LandingFoilMarkProps) {
  const reduced = useReducedMotion();
  const dim = dimensionPx(size);
  const letterClass = letterFontClass(size);

  const initial = reduced
    ? { opacity: 0 }
    : { scale: 0, rotate: -15, opacity: 0 };
  const animate = reduced
    ? { opacity: 1 }
    : { scale: 1, rotate: 0, opacity: 1 };
  const transition = reduced
    ? { duration: 0.2, ease: "easeOut" as const }
    : {
        type: "spring" as const,
        stiffness: 200,
        damping: 18,
        delay: animationDelay,
      };

  return (
    <motion.div
      role="img"
      aria-label="Foundry logo"
      initial={initial}
      animate={animate}
      transition={transition}
      className={cn("relative inline-flex items-center justify-center", className)}
    >
      <div
        aria-hidden
        className={cn(
          "relative overflow-hidden rounded-full shadow-lg shadow-amber-500/30",
          "ring-1 ring-white/10",
        )}
        style={{ width: dim, height: dim }}
      >
        {/* Outer holographic ring — amber brand palette */}
        <div
          className={cn(
            "absolute inset-0 rounded-full bg-gradient-to-br",
            "from-amber-400/70 via-orange-300/30 to-amber-500/60",
          )}
        />
        {/* Inner surface with glossy highlight */}
        <div
          className={cn(
            "absolute inset-[3px] rounded-full",
            "bg-[radial-gradient(circle_at_30%_30%,_rgba(255,255,255,0.25)_0%,_transparent_40%)]",
            "bg-surface",
          )}
        />
        {/* Conic foil shimmer — paused under reduced-motion */}
        <div
          className={cn(
            "absolute inset-0 rounded-full",
            "bg-[conic-gradient(from_0deg,_rgba(255,255,255,0.10),_transparent_25%,_rgba(255,255,255,0.10)_50%,_transparent_75%,_rgba(255,255,255,0.10))]",
            !reduced && "animate-[spin_12s_linear_infinite]",
          )}
        />
        {/* Letter "F" — amber-200 brand, Bricolage bold */}
        <div className="relative flex h-full w-full items-center justify-center">
          <span
            className={cn(
              "font-heading font-bold leading-none text-amber-200",
              letterClass,
            )}
          >
            F
          </span>
        </div>
      </div>
    </motion.div>
  );
}
